import React, { useRef } from "react";
import { TrashIcon } from "src/lib/ui-icon-v2";
import { IconButtonV2 } from "../../../IconButtonV2/IconButtonV2.tsx";
import * as styles from "./PreviewRow.style.ts";
import type { ChangeEvent, MouseEvent } from "react";
import type {
  Config,
  MediaObject,
} from "src/components/DropZoneInputV2/types.ts";
import { useDrag, useDrop } from "react-dnd";
import { composeRefs } from "@exonn/hooks";
import Drop from "./drop.svg?react";
import { useMutation } from "@tanstack/react-query";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { useSnackbar } from "src/App/SnackbarProvider.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum as TNS } from "src/lib/i18next/types.ts";

type RowProps = {
  onRemove(): void;
  draggedItemId: null | string;
  onChangeDraggedItemId: (draggedItemId: null | string) => void;
  index: number;
  object: MediaObject;
  disableDragging?: boolean;
  media: Array<MediaObject>;
  onReplace(newFile: File): void;
  validationRules?: Partial<Config["validationRules"]>;
  onMove: (item: MediaObject, moveToIndex: number) => void;
  additionalButtons?: (media: MediaObject) => React.ReactNode[];
  disableReplaceMedia?: boolean;
  disablePreviewPicture?: boolean;
};

function downloadFile(file: Blob, name = "file") {
  const url = URL.createObjectURL(file);

  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.style.display = "none";

  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// todo: show uploading progress
export const PreviewRow = (props: RowProps) => {
  const {
    onRemove,
    onReplace,
    onChangeDraggedItemId,
    disableDragging,
    draggedItemId,
    onMove,
    media,
    object,
    index,
    validationRules,
    additionalButtons,
    disableReplaceMedia,
    disablePreviewPicture,
  } = props;
  const snackbar = useSnackbar();
  const imageName = object.attributes.file_name || object.attributes.name;
  const { t } = useTranslation(TNS.DropZoneInputComponentV2);

  const draggable = media.length > 1 && !disableDragging;

  const url =
    object.attributes.preview_url ||
    object.attributes.original_url ||
    // object.client_preview || it is not working right for .csv files
    undefined;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const accept = validationRules?.accept || [];

  const isDragging = draggedItemId === object.attributes.uuid;
  const localRef = useRef<HTMLDivElement | null>(null);

  const preventReplaceMediaClick = useRef<boolean>(false);

  const [, drag] = useDrag(
    () => ({
      type: "default",
      canDrag: !disableDragging,
      item: () => {
        onChangeDraggedItemId(object.attributes.uuid);
        preventReplaceMediaClick.current = true;
        return object;
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        onChangeDraggedItemId(null);
      },
    }),
    [object, onChangeDraggedItemId],
  );

  const [, drop] = useDrop<MediaObject>(
    () => ({
      accept: "default",
      hover(draggedImage, monitor) {
        if (draggedImage.attributes.uuid === object.attributes.uuid) {
          return;
        }
        const draggedImageIndex = media.findIndex(
          (media) => media.attributes.uuid === draggedImage.attributes.uuid,
        );

        const boundingRect = localRef.current?.getBoundingClientRect();

        if (!boundingRect) {
          return;
        }

        const clientOffset = monitor.getClientOffset();

        if (!clientOffset) {
          return;
        }

        // Moving up
        if (
          draggedImageIndex > index &&
          clientOffset.y >
            boundingRect.bottom - (boundingRect.bottom - boundingRect.top) / 2
        ) {
          return;
        }

        // Moving down
        if (
          draggedImageIndex < index &&
          clientOffset.y <
            boundingRect.top + (boundingRect.bottom - boundingRect.top) / 2
        ) {
          return;
        }

        onMove(draggedImage, index);
      },
    }),
    [onMove, object, index, media],
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.files &&
      event.target.files[0] &&
      onReplace(event.target.files[0]);
  };

  const handleReplace = () => {
    if (disableReplaceMedia) {
      return;
    }
    if (!preventReplaceMediaClick.current) {
      inputRef.current && inputRef.current.click();
    }
    preventReplaceMediaClick.current = false;
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove();
  };
  const exonnApi = useExonnApi();
  const { mutate: downloadMedia } = useMutation({
    mutationFn: () => {
      if (!object.attributes.is_private && object.attributes?.id) {
        return exonnApi.client.get(`media/${object.attributes.id}`, {
          withCredentials: true,
          withXSRFToken: true,
          responseType: "blob",
        });
      }
      return exonnApi.client.get(
        `${
          object.attributes.original_url
            ? object.attributes.original_url
            : object.attributes.preview_url
        }`,
        {
          withCredentials: true,
          withXSRFToken: true,
          responseType: "blob",
        },
      );
    },
    onSuccess: (data) => {
      downloadFile(data.data, object.attributes.name);

      snackbar.success({
        flash: true,
        message: t("snackbar.success_download"),
      });
    },
    onError: () => {
      snackbar.error({ flash: true, message: t("snackbar.error") });
    },
  });

  const handleDownload = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    downloadMedia();
  };

  return (
    <div
      ref={composeRefs(
        (node) => drag(node),
        (node) => drop(node),
        (node) => (localRef.current = node),
      )}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onPointerUp={handleReplace}
      css={styles.root({
        isDragging,
      })}
    >
      {draggable && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          title={"Drag"}
          className={"drop"}
          css={styles.drop({
            isDragging,
          })}
        >
          <Drop />
        </button>
      )}

      <input
        type="file"
        accept={accept.length > 0 ? accept.join(",") : undefined}
        hidden
        ref={inputRef}
        onChange={handleFileChange}
      />

      {!disablePreviewPicture && url && (
        <div css={styles.imageContainer}>
          <img src={url} alt={imageName} css={styles.image} />
        </div>
      )}

      <span css={styles.placeholder}>{imageName}</span>

      {(additionalButtons?.(object) || []).map((button, index) => (
        <React.Fragment key={index}>{button}</React.Fragment>
      ))}

      <IconButtonV2
        skin={"info"}
        size={"sm"}
        variant={"outlined"}
        type={"button"}
        css={styles.removeButton}
        onPointerUp={handleDownload}
      >
        <span className={"icon-15-download"} />
      </IconButtonV2>

      <IconButtonV2
        size="sm"
        skin="danger"
        variant="outlined"
        type="button"
        css={styles.removeButton}
        onPointerUp={handleRemove}
      >
        <TrashIcon />
      </IconButtonV2>
    </div>
  );
};
